import { Button, ButtonGroup, CircularProgress, Container, Divider, Grid, InputAdornment, MenuItem, TextField, Typography, } from "@mui/material";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Box, padding, textAlign } from "@mui/system";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactLoading from "react-loading";
import { useLocation, useNavigate } from "react-router-dom";
import { PROMO_CODE, PROVIDER_ID, PROVIDER_NAME, REPs, ThreeReps, Tier, CONSTELLATION_UTIL_MAPPING } from "../config";
import { fetchConstellationPlans, fetchFliterMonth, fetchPlan, fetchThreeHeightsPlan, fetchUtility, } from "../fetch/fetch";
import useIpZipCode from "../hooks/useIpZipCode";
import { ACPlans, ACRows, AddressProps, ChartDataTypes, PlanProps, Utilities, UtilityTypes, } from "../types";
import { CompareChart } from "./CompareChart";
import { RollingNumber } from "./RollingNumber";
import { withSnackbar } from "./SnackbarHOC";
import GoogleSwiper from "./Swiper";
import { getBestRate, getBestValue, getPlanName, pushToDataLayer } from "./data";
import { EntrySection } from "./styles/common.styled";
import { ChoiceSection, EvaluateSection, HomeIntroContentSection, HomeIntroSection, IntroContentSection, IntroSection, SectionWrapper, TickSection, } from "./styles/saving-section.styled";
import { PlanModal } from "./v2/enrollment/PlanModal";
import CompareGrid from "./v2/CompareGrid";
import { ErrorModal } from "./v2/enrollment/ErrorModal";
import { MyAddressContext } from "../context-manager";
import { GetUsageSidebar } from "./GetUsageSidebar";


const SavingSection = ({ snackbarShowMessage, }: { snackbarShowMessage?: Function; }) => {
  const location = useLocation();
  const [zipcode, setZipcode] = useState("");
  const [selectedTerm, setSelectedTerm] = useState("12");
  const [utilities, setUtilities] = useState<UtilityTypes[]>([]);
  const [selectedUtility, setSelectedUtility] = useState("");
  const [termOptions, setTermOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [avgMonthlyUse, setAvgMonthlyUse] = useState("1000");
  const [chartData, setChartData] = useState<ChartDataTypes[]>([]);
  const [acData, setAcData] = useState<ChartDataTypes[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [oneRepPlanList, setOneRepPlanList] = useState<PlanProps[]>([]);
  const [plans, setPlans] = useState<{ [key: string]: PlanProps[] } | undefined>(undefined);
  const [utility, setUtility] = useState<Utilities | undefined>(undefined);
  const [products, setProducts] = useState<PlanProps[]>([]);
  const mainRef = useRef<null | HTMLDivElement>(null);
  const [threeHeightPlans, setThreeHeightPlans] = useState<{ [key: string]: PlanProps[] } | undefined>();
  const [isThreePlan, setIsThreePlan] = useState(false);
  const [chartLoading, setChartLoading] = useState<boolean>(false);
  const [showAC, setShowAC] = useState<boolean>(false);
  const [showTerm, setShowTerm] = useState<boolean>(false);
  const [errorField, setErrorField] = useState<any>();
  const [acPlans, setACPlans] = useState<ACPlans[]>([]);
  const [acRow, setACRows] = useState<ACRows[]>([]);
  const [promoCode, setPromoCode] = useState<string>();  // To persist the tier 1 data for chart in three heights plan
  const [tier1Data, setTier1Data] = useState<ChartDataTypes[]>([]);
  const mediaQuery = useMediaQuery("(max-width:430px)");
  const isComparePage = location.pathname === "/";
  const { ipZipCode, isFetching } = useIpZipCode();  
  const inputZipCodeRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  useEffect(() => { if (ipZipCode) { inputZipCodeRef.current!.value = ipZipCode; setZipcode(ipZipCode); } }, [ipZipCode]);
  // Set the promocode from the url params
  useEffect(() => {
    setPromoCode((location.pathname.substring(1) as string) || PROMO_CODE);   // Storing Promocode for retreving in Enrollment Funnel
    localStorage.setItem("promoCode", (location.pathname.substring(1) as string) || PROMO_CODE);
  }, [location]);
  // If user enters the zipcode fetch the utility
  useEffect(() => {
    if (zipcode.length === 5 && !loading) {
      setShowTerm(false);
      setErrorField("");
      setProducts([]);
      setLoading(true);
      setIsThreePlan(false);
      setSelectedUtility("");
      setUtilities([]);
      setSelectedTerm("");
      setChartData([]);
      getUtility();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipcode]);
  // Fn to fetch the utilities
  const getUtility = () => {
    fetchUtility(zipcode).then((res) => {
      if (res && res[0] && res[0].utility_code) {
        setUtilities(res);
        if (res && res.length === 1) { setShowTerm(true); }
        if (res[0] && res[0].utility_code) {
          setSelectedUtility(res[0].utility_name);
          localStorage.setItem("utility", JSON.stringify(res[0]));
        }
      } else {
        // snackbarShowMessage?.(res.message, 'error');
        setErrorField(res.message);
        setLoading(false);
      }
    });
  };
  // Once user selects the utility fetch the terms[12,24,36] for usage
  useEffect(() => {
    const utilityCode = findUtilityCode();
    if (utilityCode) {
      setSelectedTerm("");
      setLoading(true);
      fetchFliterMonth(zipcode, utilityCode, promoCode).then((res) => {
        if (res && Number(res.status) === 1) {
          let terms = res.response.terms;
          terms.sort((a: string, b: string) => {
            if (parseInt(a) < parseInt(b)) return -1;
            if (parseInt(a) > parseInt(b)) return 1;
            return 0;
          });
          // Adding 36 month plan
          // terms.push("36")
          setTermOptions(terms);
          if (terms.length > 0) {
            if (terms.indexOf("12") >= 0) {
              setSelectedTerm("12");
            } else {
              setSelectedTerm(terms[0]);
            }
          }
        } else {
          snackbarShowMessage?.(res.msg, "error");
          setLoading(false);
        }
        setLoading(false);
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUtility]);

  // If user selects term and usage then fetch the plans and products
  useEffect(() => {
    if (selectedTerm && selectedUtility) {
      if (utilities.length > 1 && chartData.length === 0) {
        setLoading(false);
      } else {
        setChartData([]);
        setIsThreePlan(false);
        setPlans(undefined);
        setProducts([]);
        getProducts();
        getPlan();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTerm, avgMonthlyUse]);
  // Remove any existing addresses,plans in the local storage
  useEffect(() => {
    localStorage.removeItem("selectedAddress");
    localStorage.removeItem("ts_plan");
  }, []);
  // Fn to check if the utility is AC
  useEffect(() => {
    if (
      selectedUtility &&
      utilities.filter(
        (item) =>
          item.utility_name.indexOf("CenterPoint Energy") > -1 ||
          item.utility_name.indexOf("Oncor") > -1
      ).length !== 0
    ) {
      setShowAC(true);
    } else {
      setShowAC(false);
    }
  }, [utilities]);

  // Handle scroll when user clicks on view plans
  const handleScroll = () => {
    if (mainRef && mainRef.current) {
      mainRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };
  // Util fn to get the utility code for util name
  const findUtilityCode = () => {
    if (!selectedUtility) return "";
    return utilities.filter((val) => val.utility_name === selectedUtility)[0]
      .utility_code;
  };
  // Fetching constellation Plans
  const getProducts = (myUsage?: Number[]) => {
    setLoading(true);
    const utilityCode = findUtilityCode();
    const term = selectedTerm ? selectedTerm : "12";
    const provider_id = PROVIDER_ID;
    const usages = new Array(12).fill(parseInt(avgMonthlyUse));

    if (!isComparePage) {
      fetchConstellationPlans(
        zipcode,
        utilityCode,
        term,
        provider_id,
        usages,
        promoCode,
      ).then((res) => {
        setLoading(false);
        if (res && Number(res.status) === 1) {
          const { plans } = res.response;
          if (plans) {
            var _plans = plans.filter(
              (item: any) => item.plan_name.indexOf("Flat Tier Product") === -1
            );
            setProducts(_plans);
          }
          if (res.response.utility) {
            setUtility(res.response.utility)
            localStorage.setItem(
              "utility",
              JSON.stringify(res.response.utility)
            );
          }
        } else {
          setProducts([]);
        }
      });
    } else {
      fetchConstellationPlans(
        zipcode,
        utilityCode,
        term,
        provider_id,
        usages,
        promoCode
      ).then((res) => {
        setLoading(false);
        if (res && Number(res.status) === 1) {
          const { plans } = res.response;
          if (plans) {
            var _plans = plans.filter(
              (item: any) => item.plan_name.indexOf("Flat Tier Product") === -1
            );
            setProducts(_plans);
          }
          if (res.response.utility) {
            setUtility(res.response.utility)
            localStorage.setItem(
              "utility",
              JSON.stringify(res.response.utility)
            );
          }
        } else {
          // Adding the 36 month plan
          // setProducts([thirtSixPlan]);
          setProducts([]);
        }
      });
    }
  };
  // Fetch the competitor prices
  const getPlan = () => {
    const usages = new Array(Number(selectedTerm)).fill(
      parseInt(avgMonthlyUse)
    );

    const utilityCode = findUtilityCode();
    setLoading(true);
    setChartLoading(true);
    let tempSelectedTerm;
    fetchPlan(
      parseInt(tempSelectedTerm || selectedTerm),
      utilityCode,
      usages
    ).then((res) => {
      setLoading(false);
      if (res && res.code === 1) {
        const allPlans = res.response;
        setPlans(allPlans);
        // setTimeout(() => {  handleScroll(); }, 200);
      }
      if (res && res.code === 0) {getThreeHeightsPlan();}
    });
  };
  // Fetch the three heights plans
  const getThreeHeightsPlan = () => {
    const usages = new Array(Number(selectedTerm)).fill(
      parseInt(avgMonthlyUse)
    );
    const utilityCode = findUtilityCode();
    setIsThreePlan(true);
    fetchThreeHeightsPlan(
      parseInt(selectedTerm ? selectedTerm : "12"),
      utilityCode,
      usages
    ).then((res) => {
      if (res && res.code === 1) { const allPlans = res.response; setThreeHeightPlans(allPlans);
      } else {
        snackbarShowMessage?.(res.msg, "error");
      }
    });
  };

  // If any of products or plans change recalculate the average rate
  useEffect(() => {
    getAverageRate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, plans]);
  // Fn to calculate the average rate
  const getAverageRate = () => {
    if (!plans) {
      return;
    }
    if (products.length !== 0 && plans) {
      let allPlans: any = plans;
      const BestRate = getBestRate(promoCode);
      const BestValue = getBestValue(promoCode);
      
      let _products: PlanProps[] = [];
      if (promoCode === PROMO_CODE) {
        _products = products.filter(
          (item) =>
            BestRate.indexOf(Number(item.plan_id)) ||
            BestValue.indexOf(Number(item.plan_id))
        );
      } else {
        _products = products.filter(
          (item: PlanProps) =>
            (BestRate.indexOf(Number(item.plan_id)) ||
              BestValue.indexOf(Number(item.plan_id))) &&
            Number(item.contract_term) === 12
        );
      }
      // Price Type is value to be used based on monthly usage like price500,price1000
      const priceType = "price" + avgMonthlyUse;
      // This is constellations cheapest plan rate
      let total_price: number;
      let ac_total_price: number;
      let plan_name = '';

      if (_products[0][priceType as keyof PlanProps]) {
        total_price = Number(
          _products.sort((a, b) => {
            return a.rate - b.rate;
          })[0][priceType as keyof PlanProps]
        );
        plan_name = _products?.sort((a, b) => {
          return a.rate - b.rate;
        })[0].plan_name;
      } else {
        total_price =
          (Number(
            _products.sort((a, b) => {
              return a.rate - b.rate;
            })[0].month[1]
          ) /
            Number(avgMonthlyUse)) *
          100;
        plan_name = _products?.sort((a, b) => {
          return a.rate - b.rate;
        })[0].plan_name;
      }

    

      ac_total_price = Number(
        _products[
        _products.findIndex((item: any) =>
          item.plan_name.includes("A/C Protect Plus")
        )
        ]?.["price1000"] || 0
      );
      let data: any = [];
      // This is for AC Grid in Home Page
      let ac_data: any = [];
      for (let i in allPlans) {
        const _plans = allPlans[i];
        // Sort the plans for each rep in descending order
        _plans.sort((a: any, b: any) => {
          if (a.total_rate < b.total_rate) return -1;
          if (a.value > b.value) return 1;
          return 0;
        });
        if (i !== PROVIDER_ID) {
          let ind = 0;
          let ac_ind = 0;
          if (i === REPs[1].providerId) {
            ac_ind = _plans.findIndex((item: any) =>
              item.plan_name.includes("Live Brighter")
            );
          } else if (i === REPs[2].providerId) {
            ac_ind = _plans.findIndex((item: any) =>
              item.plan_name.includes("Simple Rate")
            );
          } else if (i === REPs[3].providerId) {
            ac_ind = _plans.findIndex((item: any) =>
              item.plan_name.includes("Secure Advantage")
            );
          }
          if (_plans[ind]?.total_rate) {
            data.push({
              id: i,
              value: Number(_plans[ind][priceType]),
              label:
                _plans[ind].provider_name ||
                REPs.filter((item) => item.providerId === i)[0].providerName,
              planName: _plans[ind].plan_name,
              productName: _plans[ind].product_name,
            });
          }
          if (_plans[ac_ind]?.total_rate) {
            ac_data.push({
              id: i,
              value: Number(_plans[ac_ind][priceType]),
              label:
                _plans[ac_ind].provider_name ||
                REPs.filter((item) => item.providerId === i)[0].providerName,
              planName: _plans[ac_ind].plan_name,
              productName: _plans[ac_ind].product_name,
            });
          }
        }
      }


      if ((data.filter((item: any) => Number(item.value) < Number(total_price))
        .length > 0 ||
        data.length < 3)
      ) {        
        setTier1Data( data.filter((item: any) => Number(item.value) > Number(total_price)));
        getThreeHeightsPlan();
      } else {
        // Sort data in descending order by value
        data.sort((a: any, b: any) => {
          if (a.value < b.value) return -1;
          if (a.value > b.value) return 1;
          return 0;
        });

        let _chartData: any = [];
        let _acChartData: any = [];
        // Add current provider's data as the first item
        _chartData.push({
          id: PROVIDER_ID,
          // value: Math.ceil(total_price / products.length * 100) / 100,
          value: total_price,
                  // TODO: inject the planName
          label: PROVIDER_NAME,
          planName: plan_name,
          
        });
        
        _acChartData.push({
          id: PROVIDER_ID,
          // value: Math.ceil(total_price / products.length * 100) / 100,
          value: ac_total_price,
                    // TODO: inject the planName
          label: PROVIDER_NAME,
          planName: plan_name,
          
        });
        
        // Show the cheapest three in the compare chart
        if (data.length >= 3) {
          _chartData = _chartData.concat(data.slice(-3));
        } else {
          _chartData = _chartData.concat(data.slice(-1 * data.length - 1));
        }

        const txuSaversChoice = plans && plans[176]?.find(
          (plan: PlanProps) =>
            plan.plan_name.toLowerCase().trim() === "saver's choice 12"

        );
        const newChartData = _chartData.map((item: any, index: number) => {
          // forcefully setting the saversChoice plan as per requirements, if exists
          if (item.label === "Txu Energy" && String(txuSaversChoice?.plan_name).toLowerCase().trim() === "saver's choice 12") {
            return {
              "id": '176',
              "value": Number((txuSaversChoice as any)?.[priceType]), // TODO: check which price are they choosing for other plans, plan.price1000 or plan.rate Number((txuSaversChoice as any)?.[priceType])
              "label": "Txu Energy",
              "planName": txuSaversChoice?.plan_name
            }

          } else if (txuSaversChoice?.plan_name && index === 1 && _chartData.find((item: any) => item.id === '176')?.id !== '176') {
            return {
              "id": '176',
              "value": Number((txuSaversChoice as any)?.[priceType]), // TODO: check which price are they choosing for other plans, plan.price1000 or plan.rate
              "label": "Txu Energy",
              "planName": txuSaversChoice?.plan_name
            }
          }
          return item
        })
        _acChartData = _acChartData.concat(ac_data.slice(-3));
        
        setChartData( newChartData.filter((item: any) => item.value >= _chartData[0].value) );
        setAcData( _acChartData.filter( (item: any) => item.value >= _acChartData[0].value ) );
        setChartLoading(false);
        setTimeout(() => { handleScroll(); }, 200);
      }
    }
  };

  useEffect(() => {
    if (isThreePlan) { getThreeHeightsRate(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threeHeightPlans, products]);

  const getThreeHeightsRate = (flag?: boolean) => {
    if (products.length === 0 || !threeHeightPlans || chartData.length !== 0)
      return;
    let priceType = "price" + avgMonthlyUse;
    let total_price = 0;
    let plan_name = '';

    
    if (products[0][priceType as keyof PlanProps]) {
      let sorted_products = products.sort((a, b) => {
        return a.rate - b.rate;
      });
      total_price = Number( sorted_products[0][priceType as keyof PlanProps] );
      plan_name = sorted_products[0].plan_name;
      //plan_name = (avgMonthlyUse === "500" || avgMonthlyUse === "1000") ? sorted_products[0].plan_name : sorted_products[1].plan_name;
    } else {
      total_price = (Number( products.sort((a, b) => {
            return a.rate - b.rate;
          })[0].month[1]
        )
         / Number(avgMonthlyUse)) * 100;
    }
    
    let _allplans: any = threeHeightPlans;
    const data = [];
    for (let i in _allplans) {
      if (Tier.includes(Number(i)) || flag) {
        const _plans = _allplans[i];
        _plans.sort((a: any, b: any) => {
          if (a.total_rate < b.total_rate) return -1;
          if (a.value > b.value) return 1;
          return 0;
        });

        
      
        if (i !== PROVIDER_ID) {
          data.push({
            id: i,
            value: _plans[0].total_rate,
            label: _plans[0].provider_name,
            planName: _plans[0].plan_name,

          });
        }
      } else { continue; }
    }

    data.sort((a, b) => {
      if (a.value < b.value) return -1;
      if (a.value > b.value) return 1;
      return 0;
    });
    let _chartData: any = [];
    _chartData = _chartData.concat(tier1Data);
    _chartData = _chartData.concat(data.slice(-1 * (3 - tier1Data.length)));
    _chartData.sort((a: any, b: any) => {
      if (a.value < b.value) return -1;
      if (a.value > b.value) return 1;
      return 0;
    });
    // Add current provider's data as the first item
    _chartData.unshift({
      id: PROVIDER_ID,
      value: total_price,
      label: PROVIDER_NAME,
      planName: plan_name,
    });

    const txuSaversChoice = plans && plans[176]?.find( (plan: PlanProps) => plan.plan_name.toLowerCase().trim() === "saver's choice 12" );
    const newChartData = _chartData.map((item: any, index: number) => {
      // forcefully setting the saversChoice plan as per requirements, if exists
      if (item.label === "Txu Energy" && String(txuSaversChoice?.plan_name).toLowerCase().trim() === "saver's choice 12") {
        return {
          "id": '176',
          "value": Number((txuSaversChoice as any)?.[priceType]),
          "label": "Txu Energy",
          "planName": txuSaversChoice?.plan_name
        }
        // if Txu Energy does not exist, replace the second element with txusaverschoice
      } else if (txuSaversChoice?.plan_name && index === 1 && _chartData.find((item: any) => item.id === '176')?.id !== '176') {
        return {
          "id": '176',
          "value": Number((txuSaversChoice as any)?.[priceType]), 
          "label": "Txu Energy",
          "planName": txuSaversChoice?.plan_name
        }
      }
      return item
    })
       

        setChartData( newChartData.filter((item: any) => item.value >= _chartData[0].value) );
    setChartLoading(false);
    setTimeout(() => {  handleScroll(); }, 200);
  };

  // Fn to handle zipcode change
  const handleChangeZipcode = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNums = evt.target.value.replace(/[^0-9]/g, "");
    setZipcode(onlyNums);
  };
  // Code to calculate the energysavings
  let energySavings = 0;
  if (chartData && chartData.length > 0) {
    const rateDiff = chartData[chartData.length - 1]
      ? chartData[chartData.length - 1].value - chartData[0].value
      : 0;
    energySavings =
      (rateDiff * parseFloat(avgMonthlyUse) * parseInt(selectedTerm)) / 100;
  }
  // If user lands on the AC offer codes then fetch the ac plans
  useEffect(() => {
    const fetchACPlans = async () => {
      const data = await fetch("constants/AC_PLANS.json");
      const resp = await data.json();
      setACPlans(resp["ONCOR_CNP_PLANS"]);
      setACRows(resp["ONCOR_CNP_ROWS"]);
    }
    fetchACPlans();
  }, []);

  return (
    <SectionWrapper>
      <section className="cta" style={{ background: "#3286D3" }}>
        <div className="container py-5">
          <div className="row">
            <div className="col-12 col-md-1"></div>
            <div className="col-12 col-md-8">
              <Grid
                container
                position="relative"
                alignItems="left"
                justifyContent={isComparePage ? "left" : 'space-between'}
                sx={{
                  pt: 3,
                  pb: 3,
                  backgroundColor: "#fff",
                  borderRadius: 2,
                }}
                style={{ background: "#3286D3" }}
              >
                <Grid
                  item
                  xs={12}
                  sm={8}
                  md={8}
                  position="relative"
                  zIndex="1"
                  textAlign={{ xs: "center", md: "left" }}
                  pr={{ md: 4 }}
                  pb={{ xs: 3, sm: 3 }}
                  pt={{ md: 2 }}
                >
                  <>
                    {/* <h2 style={{ marginTop: 0, fontWeight: 700, color: "#fff" }}>
                      Raise your expectations, <strong style={{ color: "#CADB2E" }}>lower</strong> your <strong style={{ color: "#CADB2E" }}>rate</strong>
                    </h2> */}
                    <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: "25px", sm: "36px", md: "40px" },
                      marginTop: 0,
                      fontWeight: 700,  
                      color: "#fff",
                    }}
                    >
                       {/* It’s time to, <strong style={{ color: "#CADB2E" }}>SWITCH </strong> your electricity provider. See if you can <strong style={{ color: "#CADB2E" }}>SAVE</strong> with Constellation                         */}
                        Raise your expectations, <strong style={{ color: "#CADB2E" }}>lower</strong> your <strong style={{ color: "#CADB2E" }}>rate</strong>
                    </Typography> 
                    <p style={{ color: "#fff", fontSize: "19px" }}>Enter your zip code to see how Constellation can help you save.</p>
                    {selectedUtility && (
                      <p
                        style={{
                          paddingTop: "10px",
                          fontSize: "19px",
                          textAlign: "left",
                          color: "#fff"
                        }}
                      >
                        Showing results for {selectedUtility}
                      </p>
                    )}
                    <Grid
                      container
                      spacing={2}
                      sx={{
                        mt: { xs: 1, sm: 0 },
                        textAlign: "center",
                        alignItems: "flex-start",
                      }}
                    >
                      <Grid item xs={12} md={12}>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", sm: "row" },
                              // flexWrap: "wrap",
                              gap: 2,
                              width: "100%",
                              alignItems: { xs: "stretch", sm: "center" },
                            }}
                          >
                            <TextField
                              label=""
                              inputRef={inputZipCodeRef}
                              inputProps={{ maxLength: 5 }}
                              InputProps={{
                                endAdornment:
                                  loading || isFetching ? (
                                    <InputAdornment position="end">
                                      <ReactLoading
                                        type="bubbles"
                                        color="#bbb"
                                        width={36}
                                        height={36}
                                      />
                                    </InputAdornment>
                                  ) : null,
                              }}
                              sx={{
                                 minWidth: 175,
                                borderRadius: "4px",
                                width: { xs: "100%", sm: "60%" },
                                "& .MuiInputBase-root": {
                                  height: 48,
                                  paddingTop: { sm: "12.5px" },
                                  paddingBottom: { sm: "12.5px" }
                                },
                                "& .MuiInputBase-input": {
                                  textAlign: { xs: "center", sm: "left" },
                                  paddingTop: { sm: "12.5px" },
                                  paddingBottom: { sm: "12.5px" }
                                },
                              }}
                              disabled={loading}
                              className={errorField ? "errorTextField" : ""}
                            />
                            <Button
                              size="medium"
                              variant="secondary"
                              onClick={() => {
                                if (inputZipCodeRef.current) {
                                  const value = inputZipCodeRef.current.value;
                                  handleChangeZipcode({ target: { value } } as React.ChangeEvent<HTMLInputElement>);
                                }
                              }}
                              sx={{
                                width: { xs: "100%", sm: "auto" },
                                mt: { xs: 1, sm: 0 },
                                height: 48, // Match TextField height
                                minWidth: 175, // Optional: ensure button isn't too small
                                boxSizing: "border-box",
                              }}
                            >
                              Change Location
                            </Button>
                          </Box>
                          {errorField && (
                            <p
                              style={{
                                color: "#d32f2f",
                                textAlign: "left",
                                fontSize: "12px",
                                marginTop: "3px",
                              }}
                            >
                              {errorField}
                            </p>
                          )}
                        </div>
                      </Grid>
                      {utilities && utilities.length > 1 && (
                        <Grid item xs={12} md={5}>
                          <Select
                            labelId="utility-label"
                            id="utility"
                            value={selectedUtility}
                            onChange={(evt: SelectChangeEvent) => {
                              setSelectedUtility(evt.target.value as string);
                            }}
                            disabled={loading}
                            fullWidth
                            sx={{ textAlign: "left" }}
                          >
                            {utilities.map((val: any, idx) => (
                              <MenuItem value={val.utility_name} key={idx}>
                                {val.utility_name}
                              </MenuItem>
                            ))}
                          </Select>
                        </Grid>
                      )}
                    </Grid>
                    {utilities && utilities.length > 1 && (
                      <Box
                        sx={{ mt: 4, textAlign: { xs: "center", sm: "left" } }}
                      >
                        <Button
                          size="large"
                          variant="contained"
                          onClick={() => {
                            setShowTerm(true);
                            getPlan();
                            getProducts();
                          }}
                          disabled={loading || !zipcode || !selectedUtility}
                        >
                          Continue
                        </Button>
                      </Box>
                    )}
                  </>
                </Grid>
              </Grid>
            </div>
            <div className="col-12 col-md-4"></div>
          </div>
        </div>
        <div className="hero-image">
          <picture>
            <img
              src="/images/img-girl.png"
              alt="raise your expectations"
            />
          </picture>
        </div>
      </section>
      <Container ref={mainRef} className="main-container">
        {selectedTerm && selectedUtility && showTerm && (
          <Box sx={{ backgroundColor: "#fff" }}>
            <h2 style={{ marginBottom: "15px" }}>
              Estimated Total Savings ={" "}
              <RollingNumber
                data={energySavings}
                styles={{ color: "#2372B9", fontWeight: 700 }}
              />
            </h2>
            <p
              style={{
                color: "#333",
                fontSize: "14px",
                fontWeight: 400,
                marginBottom: "15px",
              }}
            >
              Savings based on highest rate shown in the comparison below.
            </p>
            <Grid
              container
              flexDirection={{ xs: "column-reverse", sm: "row" }}
              alignItems="center"
              justifyContent={{ xs: "center", sm: "center" }}
            >
              <Grid
                container
                item
                xs={12}
                sm={7}
                alignItems="center"
                justifyContent={{ xs: "center", sm: "center" }}
                sx={{ marginTop: { xs: 3, sm: 0 } }}
              >
                <span style={{ marginRight: 12 }}>Avg. Monthly Use:</span>
                <ButtonGroup
                  sx={{ marginTop: { xs: 2, sm: 0 } }}
                  disabled={chartLoading}
                >
                  {["500", "1000", "2000"].map((val, idx) => (
                    <Button
                      variant={
                        avgMonthlyUse === val ? "contained" : "outlined"
                      }
                      key={idx}
                      onClick={() => {
                        setAvgMonthlyUse(val);
                      }}
                    >{`${val} kWh`}</Button>
                  ))}
                </ButtonGroup>
                {/* <SavingRow>Energy Savings: <NumberFormat value={energySavings} displayType={'text'} thousandSeparator={true} prefix={'$'} decimalScale={2} fixedDecimalScale={true} /></SavingRow> */}
              </Grid>
              {/** 
              <Grid
                container
                item
                xs={12}
                sm={5}
                alignItems="center"
                justifyContent={{ xs: "center", sm: "flex-end" }}
              >
                <div>
                  <span style={{ marginRight: 12 }}>Term:</span>
                  <Select
                    labelId="term-select-label"
                    id="term-select"
                    value={selectedTerm}
                    onChange={(evt: SelectChangeEvent) => {
                      setSelectedTerm(evt.target.value as string);
                      evt.stopPropagation();
                    }}
                    disabled={loading || chartLoading}
                  >
                    {termOptions.map((val, idx) => (
                      <MenuItem
                        value={val}
                        key={idx}
                      >{`${val} Months`}</MenuItem>
                    ))}
                  </Select>
                </div>
              </Grid>*/}
            </Grid>
            {chartLoading && (
              <CircularProgress style={{ margin: "100px 0" }} />
            )}
            {!chartLoading &&
              chartData.length > 0 &&
              chartData[0].value > 0 && (
                <CompareChart
                  chartData={chartData}
                  handleSignUp={(id: string) => {
                    setShowModal(false);
                    const hostRep = REPs.filter((val) => val.host === true);
                    if (hostRep[0] && hostRep[0].providerId === id) {
                      setOneRepPlanList(products);
                      const BestRate = getBestRate(promoCode);
                      let bestRatePlan: any = [];
                      bestRatePlan = products
                        .filter((item) => BestRate.indexOf(Number(item.plan_id)) > -1)
                        .sort((a, b) =>
                          Number(a.contract_term) < Number(b.contract_term) ? -1 : 1
                        );
                      
                      // Push analytics data before handling sign up
                      const mappedUtilityCode = findUtilityCode() ? CONSTELLATION_UTIL_MAPPING[findUtilityCode() as keyof typeof CONSTELLATION_UTIL_MAPPING] || findUtilityCode() : "";
                      const chartItem = chartData.find(item => item.id === id);
                      pushToDataLayer({
                        event: "sign_up_start",
                        electricity_plan_name: chartItem?.label || "Constellation Plan",
                        electricity_plan_rate: chartItem?.value?.toFixed(1),
                        page_path: location.pathname,
                        plan_term: bestRatePlan[0]?.contract_term || "12",
                        provider_code: mappedUtilityCode,
                        user_region: utility?.state_id || "TX",
                        zipcode: zipcode || "",
                        electricity_rate_plan_id: id,     
                        campaign_source: "",
                      });
                      
                      localStorage.setItem("selectProduct", JSON.stringify(bestRatePlan[0]));
                      navigate(`/v2/enrollment${location.search}`);
                    }
                  }}
                  handleShowPlan={(id: string) => {
                    setShowModal(true);
                    const hostRep = REPs.filter((val) => val.host === true);
                    if (hostRep[0] && hostRep[0].providerId === id) {
                      setOneRepPlanList(products);
                    } else {
                      if (plans && plans[id] && !isThreePlan) {
                        setOneRepPlanList(plans[id]);
                      }
                      if (
                        threeHeightPlans &&
                        threeHeightPlans[id] &&
                        isThreePlan
                      ) {
                        if (ThreeReps.includes(Number(id)) && plans) {
                          setOneRepPlanList(plans[id]);
                        } else {
                          setOneRepPlanList(threeHeightPlans[id]);
                        }
                      }
                    }
                  }}
                />
              )}
            {/* <div style={{ marginTop: 20 }}>
              <Button
                size="large"
                variant="secondary"
                onClick={() => {
                  // window.location.href = "https://www.constellation.com/solutions/for-your-home/residential-signup.html?zip=&promoCode=ROCKSTAR"
                  setOneRepPlanList(products);
                  setShowModal(true);
                }}
              >
                Sign Up
              </Button>
            </div> */}
          </Box>
        )}
      </Container>
      <IntroSection color="#fff">
        <Container>
          <Grid
            container
            position="relative"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid
              item
              xs={12}
              sm={6}
              position="relative"
              zIndex="1"
              textAlign="center"
              order={0}
            >
              <h2>Why Switch to Constellation?</h2>
              <p>
                Constellation offers a variety of home energy options to fit
                your budget, values and needs. Compare plan benefits and see why
                over 2 million customers choose Constellation as their trusted
                energy supplier.
              </p>
              <Button
                onClick={() => {
                  handleScroll();
                }}
                variant="text"
              >
                Back to Plans
              </Button>
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              position="relative"
              zIndex="1"
              textAlign="center"
              className="tick_grid"
            >
              <TickSection style={{ backgroundColor: "#12385D" }}>
                <img src="/images/icon-tick.png" alt="" />
                <h3>Fixed Rates</h3>
                <p>Enjoy price stability for the duration of your plan term</p>
              </TickSection>
              <TickSection style={{ backgroundColor: "#6AA443" }}>
                <img src="/images/icon-tick.png" alt="" />
                <h3>Bill Credits</h3>
                <p>Earn up to $50 back on your monthly bill</p>
              </TickSection>
              <TickSection style={{ backgroundColor: "#2073B7" }}>
                <img src="/images/icon-tick.png" alt="" />
                <h3>Easy Setup</h3>
                <p>Enroll online or over the phone in 5 minutes</p>
              </TickSection>
              <TickSection style={{ backgroundColor: "#F37C27" }}>
                <img src="/images/icon-tick.png" alt="" />
                <h3>ETF Coverage</h3>
                <p>We will cover your early termination fee up to $150</p>
              </TickSection>
            </Grid>
          </Grid>
        </Container>
      </IntroSection>
      <ChoiceSection>
        <Container>
          <h2>The Power of Choice</h2>
          <p>
            At Constellation, we take pride in offering a wide selection of
            energy plans to meet the diverse needs of our customers. However, it
            is important to note that the plans you see on this landing page do
            not represent all plans available in your market. The comparisons
            you see on here are against companies that we believe meet our
            standard of quality, but we encourage you to continue your research
            and due diligence to find the plan that best fits your needs.
          </p>
          <Button
            onClick={() => {
              handleScroll();
            }}
            variant="text"
          >
            Back to Plans
          </Button>
        </Container>
      </ChoiceSection>
      <EvaluateSection>
        <Container>
          <h3>See what our customers are saying.</h3>
          <GoogleSwiper />
          <img src="/images/google_review.png" alt="" height={150} />
        </Container>
      </EvaluateSection>
      <PlanModal
        show={showModal}
        data={oneRepPlanList}
        handleClose={() => {
          setShowModal(false);
        }}
        selectProduct={() => { }}
        promoCode={promoCode}
        avgMonthlyUse={avgMonthlyUse}
        utility={utility}
      />
    </SectionWrapper>
  );
};

export default withSnackbar(SavingSection);
